import React, { useState, useEffect, useMemo } from 'react'
import axios from 'axios'
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  Paper,
  Select,
  MenuItem,
  TextField,
  Chip,
  Typography,
  FormControl,
  InputLabel,
  CircularProgress,
  Checkbox,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions
} from '@material-ui/core'
import {
  Visibility,
  VisibilityOff,
  MergeType as MergeIcon,
  DeleteOutline as DeleteOutlineIcon,
  OpenWith as MoveIcon,
  Publish as PromoteIcon
} from '@material-ui/icons'
import Autocomplete from '@material-ui/lab/Autocomplete'
import BidItemPromotionModal from './BidItemPromotionModal'

const ITEM_TYPES = ['all', 'standard', 'lump_sum', 'allowance', 'header', 'subtotal', 'informational']
const MAPPING_STATUSES = ['active', 'all', 'unmapped', 'pending', 'mapped', 'needs_review', 'ignored']

const ITEM_TYPE_CHIP = {
  standard:      { color: 'default', style: {} },
  lump_sum:      { color: 'primary', style: {} },
  allowance:     { color: 'secondary', style: {} },
  header:        { color: 'default', style: { fontStyle: 'italic' } },
  subtotal:      { color: 'default', style: { fontStyle: 'italic' } },
  informational: { color: 'default', style: { fontStyle: 'italic' } }
}

const MAPPING_STATUS_CHIP = {
  mapped:       { color: 'default', style: { backgroundColor: '#4caf50', color: '#fff' } },
  pending:      { color: 'default', style: { backgroundColor: '#ff9800', color: '#fff' } },
  unmapped:     { color: 'default', style: {} },
  needs_review: { color: 'default', style: { backgroundColor: '#f44336', color: '#fff' } },
  ignored:      { color: 'default', style: { backgroundColor: '#9e9e9e', color: '#fff' } }
}

const getItemTypeChip = (type) => ITEM_TYPE_CHIP[type] || { color: 'default', style: {} }
const getMappingStatusChip = (status) => MAPPING_STATUS_CHIP[status] || { color: 'default', style: {} }

// Inline editable cell — click to edit, save on blur or Enter
const EditableCell = ({ value, onSave, type, options, renderDisplay }) => {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)

  const startEdit = () => {
    setDraft(value)
    setEditing(true)
  }

  const commit = () => {
    setEditing(false)
    if (draft !== value) onSave(draft)
  }

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') commit()
    if (e.key === 'Escape') setEditing(false)
  }

  if (!editing) {
    if (renderDisplay) {
      return (
        <span onClick={startEdit} style={{ cursor: 'pointer' }} title='Click to edit'>
          {renderDisplay(value)}
        </span>
      )
    }
    return (
      <span
        onClick={startEdit}
        style={{ cursor: 'pointer', display: 'inline-block', minWidth: 40 }}
        title='Click to edit'
      >
        {value !== undefined && value !== null && value !== '' ? String(value) : <em style={{ color: '#bbb' }}>—</em>}
      </span>
    )
  }

  if (type === 'select' && options) {
    return (
      <Select
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        autoFocus
        style={{ minWidth: 120 }}
      >
        {options.map(opt => (
          <MenuItem key={opt} value={opt}>{opt}</MenuItem>
        ))}
      </Select>
    )
  }

  if (type === 'autocomplete' && options) {
    return (
      <Autocomplete
        freeSolo
        options={options}
        value={draft || ''}
        onChange={(e, newVal) => setDraft(newVal || '')}
        onInputChange={(e, newVal) => setDraft(newVal || '')}
        onBlur={commit}
        openOnFocus
        size='small'
        style={{ minWidth: 100 }}
        renderInput={(params) => (
          <TextField
            {...params}
            autoFocus
            size='small'
            onKeyDown={handleKeyDown}
          />
        )}
      />
    )
  }

  return (
    <TextField
      value={draft}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={handleKeyDown}
      autoFocus
      size='small'
      type={type === 'number' ? 'number' : 'text'}
      fullWidth
      style={{ minWidth: 80 }}
    />
  )
}

const ItemsTab = ({ packageId, projectId, url, onItemUpdate, initialGroupFilter, onGroupFilterChange, sourceFile }) => {
  const [items, setItems] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [filterGroup, setFilterGroup] = useState(initialGroupFilter || 'all')
  const [filterType, setFilterType] = useState('all')
  const [filterStatus, setFilterStatus] = useState('active')

  // Source panel
  const [showSource, setShowSource] = useState(false)

  // Selection & bulk actions
  const [selected, setSelected] = useState([])
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false)
  const [moveGroupId, setMoveGroupId] = useState('')
  const [promotionOpen, setPromotionOpen] = useState(false)

  // Unit sizes for autocomplete dropdown
  const [unitSizes, setUnitSizes] = useState([])
  useEffect(() => {
    axios.get(`${url}/api/customer/plant-sizes`)
      .then(res => {
        if (res.data && res.data.sizes) setUnitSizes(res.data.sizes)
      })
      .catch(() => {}) // silently fail — text input still works
  }, [url])

  useEffect(() => {
    if (initialGroupFilter) {
      setFilterGroup(initialGroupFilter)
    }
  }, [initialGroupFilter])

  const setFilterGroupAndNotify = (val) => {
    setFilterGroup(val)
    if (onGroupFilterChange) onGroupFilterChange(val)
  }

  const fetchItems = () => {
    if (!packageId) return
    setLoading(true)
    setError('')
    axios.get(`${url}/api/bid-estimation/bid-packages/${packageId}/items`)
      .then(res => {
        setItems(res.data || [])
        setLoading(false)
      })
      .catch(err => {
        setError(err.response?.data?.message || 'Failed to load bid items.')
        setLoading(false)
      })
  }

  useEffect(() => {
    fetchItems()
  }, [packageId, url])

  const handleFieldSave = async (itemId, field, value) => {
    try {
      const payload = { [field]: value }
      const res = await axios.put(`${url}/api/bid-estimation/bid-items/${itemId}`, payload)
      const updated = res.data
      setItems(prev => prev.map(it => it._id === itemId ? { ...it, ...updated } : it))
      if (onItemUpdate) onItemUpdate(updated)
    } catch (err) {
      console.error('Failed to update bid item:', err)
    }
  }

  // Distinct groups for filter dropdown and move target
  const distinctGroups = useMemo(() => {
    const seen = {}
    return items.filter(it => {
      if (!it.bidGroup || !it.bidGroup._id || seen[it.bidGroup._id]) return false
      seen[it.bidGroup._id] = true
      return true
    }).map(it => ({ _id: String(it.bidGroup._id), name: it.bidGroup.name || '(unnamed)' }))
      .sort((a, b) => a.name.localeCompare(b.name))
  }, [items])

  const groupOptions = [{ _id: 'all', name: 'All Groups' }, ...distinctGroups]

  const needsReviewCount = items.filter(it => it.mappingStatus === 'needs_review').length

  const filtered = items.filter(it => {
    if (filterGroup !== 'all') {
      const groupId = it.bidGroup && it.bidGroup._id ? String(it.bidGroup._id) : null
      const groupName = it.bidGroup && it.bidGroup.name
      if (groupId !== filterGroup && groupName !== filterGroup) return false
    }
    if (filterType !== 'all' && it.itemType !== filterType) return false
    if (filterStatus === 'active' && it.mappingStatus === 'ignored') return false
    else if (filterStatus !== 'all' && filterStatus !== 'active' && it.mappingStatus !== filterStatus) return false
    return true
  })

  // ── Selection handlers ─────────────────────────────────────────
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelected(filtered.map(i => i._id))
    } else {
      setSelected([])
    }
  }

  const handleSelectOne = (id) => {
    setSelected(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    )
  }

  const clearSelection = () => {
    setSelected([])
    setMoveGroupId('')
  }

  // ── Bulk action handlers ───────────────────────────────────────
  const handleMerge = async () => {
    if (selected.length < 2) return
    try {
      await axios.post(`${url}/api/bid-estimation/bid-items/merge`, { itemIds: selected })
      clearSelection()
      fetchItems()
      if (onItemUpdate) onItemUpdate()
    } catch (e) {
      console.error('Merge failed:', e)
      alert('Merge failed: ' + (e.response?.data?.error || e.message))
    }
  }

  const handleBulkDelete = async () => {
    try {
      await axios.post(`${url}/api/bid-estimation/bid-items/bulk-delete`, { itemIds: selected })
      clearSelection()
      setDeleteConfirmOpen(false)
      fetchItems()
      if (onItemUpdate) onItemUpdate()
    } catch (e) {
      console.error('Delete failed:', e)
      alert('Delete failed: ' + (e.response?.data?.error || e.message))
    }
  }

  const handleBulkMove = async () => {
    if (!moveGroupId) return
    try {
      await axios.post(`${url}/api/bid-estimation/bid-items/bulk-move`, {
        itemIds: selected,
        targetGroupId: moveGroupId
      })
      clearSelection()
      fetchItems()
      if (onItemUpdate) onItemUpdate()
    } catch (e) {
      console.error('Move failed:', e)
      alert('Move failed: ' + (e.response?.data?.error || e.message))
    }
  }

  const handleMarkOutOfScope = async () => {
    if (selected.length === 0) return
    try {
      await Promise.all(selected.map(id =>
        axios.put(`${url}/api/bid-estimation/bid-items/${id}`, { mappingStatus: 'ignored' })
      ))
      clearSelection()
      fetchItems()
      if (onItemUpdate) onItemUpdate()
    } catch (e) {
      console.error('Out of scope failed:', e)
      alert('Failed: ' + (e.response?.data?.error || e.message))
    }
  }

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
        <CircularProgress />
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ padding: 16 }}>
        <Typography color='error'>{error}</Typography>
      </div>
    )
  }

  return (
    <div style={{ height: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      {/* Warning badge */}
      {needsReviewCount > 0 && (
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          backgroundColor: '#fff3e0',
          border: '1px solid #ffb300',
          borderRadius: 4,
          padding: '6px 12px',
          marginBottom: 12
        }}>
          <Typography variant='body2' style={{ color: '#e65100' }}>
            <strong>{needsReviewCount}</strong> item{needsReviewCount !== 1 ? 's' : ''} need{needsReviewCount === 1 ? 's' : ''} review
          </Typography>
        </div>
      )}

      {/* Filters + Source toggle */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <Button
          size='small'
          variant={showSource ? 'contained' : 'outlined'}
          color={showSource ? 'primary' : 'default'}
          startIcon={showSource ? <VisibilityOff /> : <Visibility />}
          onClick={() => setShowSource(!showSource)}
          disabled={!sourceFile || !sourceFile.s3Url}
          style={{ marginRight: 4 }}
        >
          {showSource ? 'Hide Source' : 'View Source'}
        </Button>

        <FormControl size='small' variant='outlined' style={{ minWidth: 200 }}>
          <InputLabel>Group</InputLabel>
          <Select
            value={filterGroup}
            onChange={e => setFilterGroupAndNotify(e.target.value)}
            label='Group'
          >
            {groupOptions.map(g => (
              <MenuItem key={g._id} value={g._id}>{g.name}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size='small' variant='outlined' style={{ minWidth: 160 }}>
          <InputLabel>Item Type</InputLabel>
          <Select
            value={filterType}
            onChange={e => setFilterType(e.target.value)}
            label='Item Type'
          >
            {ITEM_TYPES.map(t => (
              <MenuItem key={t} value={t}>{t === 'all' ? 'All Types' : t}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl size='small' variant='outlined' style={{ minWidth: 180 }}>
          <InputLabel>Mapping Status</InputLabel>
          <Select
            value={filterStatus}
            onChange={e => setFilterStatus(e.target.value)}
            label='Mapping Status'
          >
            {MAPPING_STATUSES.map(s => (
              <MenuItem key={s} value={s}>{s === 'active' ? 'Active (hide out-of-scope)' : s === 'all' ? 'All (incl. out-of-scope)' : s}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Typography variant='caption' color='textSecondary' style={{ alignSelf: 'center' }}>
          {filtered.length} of {items.length} items
        </Typography>
      </div>

      {/* Bulk action toolbar */}
      {selected.length > 0 && (
        <Paper style={{
          padding: '8px 16px',
          marginBottom: 12,
          display: 'flex',
          alignItems: 'center',
          gap: 12,
          backgroundColor: '#e3f2fd',
          flexWrap: 'wrap'
        }}>
          <Typography variant='body2' style={{ fontWeight: 600 }}>
            {selected.length} item{selected.length !== 1 ? 's' : ''} selected
          </Typography>
          <Button size='small' startIcon={<PromoteIcon />} onClick={() => setPromotionOpen(true)}
            variant='outlined' color='primary'>
            Promote to Line Items
          </Button>
          <Button size='small' startIcon={<MergeIcon />} onClick={handleMerge}
            disabled={selected.length < 2} variant='outlined'>
            Merge
          </Button>
          <FormControl size='small' variant='outlined' style={{ minWidth: 150 }}>
            <InputLabel>Move to group</InputLabel>
            <Select value={moveGroupId} onChange={e => setMoveGroupId(e.target.value)}
              label='Move to group'>
              {distinctGroups.map(g => (
                <MenuItem key={g._id} value={g._id}>{g.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          {moveGroupId && (
            <Button size='small' startIcon={<MoveIcon />} onClick={handleBulkMove}
              variant='outlined' color='primary'>
              Move
            </Button>
          )}
          <Button size='small' startIcon={<VisibilityOff />} onClick={handleMarkOutOfScope}
            variant='outlined'>
            Out of Scope
          </Button>
          <Button size='small' startIcon={<DeleteOutlineIcon />}
            onClick={() => setDeleteConfirmOpen(true)} variant='outlined' color='secondary'>
            Delete
          </Button>
          <Button size='small' onClick={clearSelection}>Clear</Button>
        </Paper>
      )}

      {/* Split layout: Source panel + Table */}
      <div style={{ display: 'flex', gap: 16, flex: 1, minHeight: 0 }}>
        {/* Source Panel */}
        {showSource && sourceFile && sourceFile.s3Url && (
          <div style={{
            width: '40%',
            minWidth: 300,
            height: '100%',
            minHeight: 0,
            overflow: 'auto',
            border: '1px solid #e0e0e0',
            borderRadius: 4,
            backgroundColor: '#fafafa',
            flexShrink: 0
          }}>
            {/\.(png|jpg|jpeg|gif)$/i.test(sourceFile.originalFilename || '') ? (
              <img
                src={sourceFile.s3Url}
                alt='Source document'
                style={{ width: '100%', display: 'block' }}
              />
            ) : /\.pdf$/i.test(sourceFile.originalFilename || '') ? (
              <iframe
                src={sourceFile.s3Url}
                title='Source PDF'
                style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
              />
            ) : (
              <div style={{ padding: 20, textAlign: 'center' }}>
                <Typography variant='body2' color='textSecondary'>
                  {sourceFile.originalFilename || 'Source file'}
                </Typography>
                <Button
                  size='small'
                  variant='outlined'
                  href={sourceFile.s3Url}
                  target='_blank'
                  style={{ marginTop: 8 }}
                >
                  Download Original
                </Button>
              </div>
            )}
          </div>
        )}

        {/* Items Table */}
        <div style={{ flex: 1, minWidth: 0, minHeight: 0, display: 'flex' }}>
          <TableContainer component={Paper} style={{ height: '100%', overflow: 'auto' }}>
            <Table size='small' stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell padding='checkbox'>
                    <Checkbox
                      indeterminate={selected.length > 0 && selected.length < filtered.length}
                      checked={filtered.length > 0 && selected.length === filtered.length}
                      onChange={handleSelectAll}
                    />
                  </TableCell>
                  <TableCell>Group</TableCell>
                  <TableCell>#</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Unit</TableCell>
                  <TableCell align='right'>Qty</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Scope</TableCell>
                  <TableCell>Mapping Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={9} align='center'>
                      <Typography variant='body2' style={{ padding: 20, color: '#999' }}>
                        No items match the current filters.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map(item => {
                    const isNeedsReview = item.mappingStatus === 'needs_review'
                    const typeChip = getItemTypeChip(item.itemType)
                    const statusChip = getMappingStatusChip(item.mappingStatus)
                    const isSelected = selected.includes(item._id)

                    return (
                      <TableRow
                        key={item._id}
                        hover
                        selected={isSelected}
                        style={isNeedsReview ? { backgroundColor: '#fff3e0' } : {}}
                      >
                        <TableCell padding='checkbox'>
                          <Checkbox
                            checked={isSelected}
                            onChange={() => handleSelectOne(item._id)}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography variant='caption' color='textSecondary'>
                            {item.bidGroup?.name || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant='caption' style={{ fontFamily: 'monospace' }}>
                            {item.itemNo || '—'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <EditableCell
                            value={item.descriptionNormalized || item.descriptionRaw || ''}
                            onSave={val => handleFieldSave(item._id, 'descriptionNormalized', val)}
                          />
                        </TableCell>
                        <TableCell>
                          <EditableCell
                            value={item.unitNormalized || item.unitRaw || ''}
                            type='autocomplete'
                            options={unitSizes}
                            onSave={val => handleFieldSave(item._id, 'unitNormalized', val)}
                          />
                        </TableCell>
                        <TableCell align='right'>
                          <EditableCell
                            value={item.quantity != null ? item.quantity : ''}
                            type='number'
                            onSave={val => handleFieldSave(item._id, 'quantity', val === '' ? null : Number(val))}
                          />
                        </TableCell>
                        <TableCell>
                          <EditableCell
                            value={item.itemType || ''}
                            type='select'
                            options={ITEM_TYPES.filter(t => t !== 'all')}
                            onSave={val => handleFieldSave(item._id, 'itemType', val)}
                            renderDisplay={(val) => {
                              if (!val) return <em style={{ color: '#bbb' }}>—</em>
                              const chip = getItemTypeChip(val)
                              return (
                                <Chip
                                  label={val}
                                  size='small'
                                  color={chip.color}
                                  style={chip.style}
                                />
                              )
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <EditableCell
                            value={item.scopeType || ''}
                            type='select'
                            options={['material', 'service', 'subcontract', 'allowance', 'combination']}
                            onSave={val => handleFieldSave(item._id, 'scopeType', val || null)}
                            renderDisplay={(val) => {
                              if (!val) return <em style={{ color: '#bbb' }}>—</em>
                              const colors = {
                                material: { bg: '#e8f5e9', color: '#2e7d32' },
                                service: { bg: '#e3f2fd', color: '#1565c0' },
                                subcontract: { bg: '#fce4ec', color: '#c62828' },
                                allowance: { bg: '#fff3e0', color: '#ef6c00' },
                                combination: { bg: '#f3e5f5', color: '#6a1b9a' }
                              }
                              const c = colors[val] || { bg: '#eee', color: '#666' }
                              return (
                                <Chip label={val} size='small'
                                  style={{ backgroundColor: c.bg, color: c.color, fontWeight: 600, fontSize: 10 }}
                                />
                              )
                            }}
                          />
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={item.mappingStatus || 'unmapped'}
                            size='small'
                            color={statusChip.color}
                            style={statusChip.style}
                          />
                        </TableCell>
                      </TableRow>
                    )
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </div>

      {/* Delete confirmation dialog */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Delete {selected.length} item{selected.length !== 1 ? 's' : ''}?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This will permanently delete the selected items. This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleBulkDelete} color='secondary' variant='contained'>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Promotion modal */}
      <BidItemPromotionModal
        open={promotionOpen}
        onClose={() => setPromotionOpen(false)}
        onPromoted={(count) => {
          setPromotionOpen(false)
          clearSelection()
          fetchItems()
          if (onItemUpdate) onItemUpdate()
        }}
        packageId={packageId}
        projectId={projectId}
        url={url}
        preSelectedIds={selected}
      />
    </div>
  )
}

export default ItemsTab
